import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface AITokens {
  openai_token?: string;
  gemini_token?: string;
  perplexity_token?: string;
  grok_token?: string;
  mistral_token?: string;
}

export function useAITokens() {
  const { user } = useAuth();
  const [tokens, setTokens] = useState<AITokens | null>(null);
  const [optimisticTokens, setOptimisticTokens] = useState<AITokens | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    fetchTokens();
  }, [user]);

  useEffect(() => {
    setOptimisticTokens(tokens);
  }, [tokens]);

  const fetchTokens = async () => {
    try {
      const { data: settings, error: fetchError } = await supabase
        .from('nrm_user_settings')
        .select('ai_tokens')
        .eq('user_id', user?.id)
        .single();

      if (fetchError) throw fetchError;
      setTokens(settings?.ai_tokens || {});
    } catch (err) {
      console.error('Failed to load AI tokens:', err);
      setError('Failed to load AI settings');
    } finally {
      setLoading(false);
    }
  };

  const updateTokens = async (updates: AITokens) => {
    if (!user) return;

    try {
      const { error: updateError } = await supabase
        .from('nrm_user_settings')
        .update({ 
          ai_tokens: updates,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (updateError) throw updateError;
      setTokens(updates);
      setOptimisticTokens(updates);
    } catch (err) {
      console.error('Failed to update token:', err);
      // Revert optimistic update on error
      setOptimisticTokens(tokens);
      throw new Error('Failed to update token');
    }
  };

  return {
    tokens: optimisticTokens,
    loading,
    error,
    updateTokens,
    refetch: fetchTokens
  };
}